import { Job } from '../types';
import { formatJobExperience, hasExperienceRange } from './experienceRange';

export type ShareMedium = 'linkedin' | 'whatsapp' | 'facebook' | 'email';

export interface ShareDraft {
  text: string;
  emailSubject?: string;
}

const formatCtc = (min?: number, max?: number, currency: string = 'INR') => {
  if (!min && !max) return 'CTC not disclosed';

  const formatAmount = (amount: number) => {
    if (currency === 'INR') {
      return `INR ${(amount / 100000).toFixed(1)} LPA`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  if (min && max) {
    return `${formatAmount(min)} - ${formatAmount(max)}`;
  }
  if (min) return `${formatAmount(min)}+`;
  return `Up to ${formatAmount(max!)}`;
};

const compact = (value?: string, fallback: string = 'Not specified') => {
  const v = (value || '').trim();
  return v || fallback;
};

const buildSkillsLine = (job: Job) => {
  if (!job.skills || job.skills.length === 0) return 'Not specified';
  const names = job.skills
    .map((item) => item.skill?.name?.trim())
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.slice(0, 8).join(', ') : 'Not specified';
};

const excerpt = (value: string | undefined, maxChars: number) => {
  const plain = compact(value, '').replace(/\s+/g, ' ').trim();
  if (!plain) return '';
  if (plain.length <= maxChars) return plain;
  return `${plain.slice(0, maxChars - 1)}...`;
};

export const buildShareDraft = (job: Job, medium: ShareMedium, jobUrl: string): ShareDraft => {
  const company = compact(job.company?.name, 'Our company');
  const location = compact(job.location, 'Remote');
  const jobType = compact(job.job_type?.replace(/_/g, ' '), 'Not specified');
  const experience = hasExperienceRange(job) ? formatJobExperience(job, { verbose: true }) : 'Not specified';
  const ctc = formatCtc(job.salary_min, job.salary_max, job.salary_currency || 'INR');
  const skills = buildSkillsLine(job);
  const summary = excerpt(job.description, 260);

  if (medium === 'linkedin') {
    return {
      text: [
        'TALIXJOBS | NOW HIRING',
        '',
        `Role: ${job.title}`,
        `Company: ${company}`,
        `Location: ${location}`,
        `Job Type: ${jobType}`,
        `Experience: ${experience}`,
        `CTC: ${ctc}`,
        `Skills: ${skills}`,
        '',
        summary ? `Role Snapshot: ${summary}` : '',
        '',
        `Apply here: ${jobUrl}`,
      ].filter(Boolean).join('\n'),
    };
  }

  if (medium === 'whatsapp') {
    return {
      text: [
        '*New Job Opportunity*',
        '',
        `*${job.title}* at *${company}*`,
        `Location: ${location}`,
        `Experience: ${experience}`,
        `CTC: ${ctc}`,
        '',
        summary ? `${summary}` : '',
        '',
        `Apply now: ${jobUrl}`,
      ].filter(Boolean).join('\n'),
    };
  }

  if (medium === 'facebook') {
    return {
      text: [
        'Career Opportunity',
        '',
        `${company} is hiring for ${job.title}.`,
        `Location: ${location} | Experience: ${experience} | CTC: ${ctc}`,
        `Key Skills: ${skills}`,
        '',
        summary ? `${summary}` : '',
        '',
        `Apply here: ${jobUrl}`,
      ].filter(Boolean).join('\n'),
    };
  }

  return {
    emailSubject: `${job.title} opening at ${company}`,
    text: [
      'Hello,',
      '',
      'I wanted to share a role that may be relevant for your network.',
      '',
      `Role: ${job.title}`,
      `Company: ${company}`,
      `Location: ${location}`,
      `Job Type: ${jobType}`,
      `Experience: ${experience}`,
      `CTC: ${ctc}`,
      `Key Skills: ${skills}`,
      '',
      summary ? `Role Snapshot: ${summary}` : '',
      '',
      `Application Link: ${jobUrl}`,
      '',
      'Best regards,',
    ].filter(Boolean).join('\n'),
  };
};

